/**
 * Overview Tab - Exports Page
 *
 * Dashboard showing export health, recent syncs, quick actions
 */

import { Card, Row, Col, Statistic, Alert, Timeline, Button, Space, Typography } from 'antd'
import {
  CloudSyncOutlined,
  ExportOutlined,
  WarningOutlined,
  SyncOutlined,
  UploadOutlined,
  SettingOutlined,
  HistoryOutlined,
} from '@ant-design/icons'
import { useQuery } from '@tanstack/react-query'
import { exportService } from '@/services/exportService'

const { Text } = Typography

interface OverviewTabProps {
  onTabChange: (tab: string) => void
}

export function OverviewTab({ onTabChange }: OverviewTabProps) {
  // Fetch export stats
  const { data: exportStats, isLoading } = useQuery({
    queryKey: ['export-stats'],
    queryFn: () => exportService.getStats(),
    staleTime: 1000 * 60, // 1 minute
  })

  const stats = exportStats?.data || {
    activeConnections: 0,
    exportsLast24h: 0,
    failedExports: 0,
    pendingUpdates: 0,
  }

  return (
    <Space direction="vertical" size="large" style={{ width: '100%' }}>
      {/* Export Health Summary */}
      <Card title="Export Health" loading={isLoading}>
        <Row gutter={16}>
          <Col span={6}>
            <Statistic
              title="Active Connections"
              value={stats.activeConnections}
              prefix={<CloudSyncOutlined />}
            />
          </Col>
          <Col span={6}>
            <Statistic
              title="Last 24h Exports"
              value={stats.exportsLast24h}
              prefix={<ExportOutlined />}
            />
          </Col>
          <Col span={6}>
            <Statistic
              title="Failed Exports"
              value={stats.failedExports}
              valueStyle={{ color: stats.failedExports > 0 ? '#cf1322' : '#3f8600' }}
              prefix={<WarningOutlined />}
            />
          </Col>
          <Col span={6}>
            <Statistic
              title="Pending Updates"
              value={stats.pendingUpdates}
              prefix={<SyncOutlined />}
            />
          </Col>
        </Row>

        {stats.failedExports > 0 && (
          <Alert
            message="Export Failures Detected"
            description={`${stats.failedExports} exports failed in the last 24 hours. Review logs for details.`}
            type="error"
            showIcon
            action={
              <Button size="small" onClick={() => onTabChange('history')}>
                View History
              </Button>
            }
            style={{ marginTop: 16 }}
          />
        )}
      </Card>

      {/* Recent Export Activity */}
      <Card
        title={
          <Space>
            <HistoryOutlined />
            <span>Recent Activity</span>
          </Space>
        }
      >
        <Timeline
          items={[
            {
              color: 'gray',
              children: (
                <Text type="secondary">
                  No recent export activity. Connect to a platform or download manual exports to get started.
                </Text>
              ),
            },
          ]}
        />
      </Card>

      {/* Quick Actions */}
      <Card title="Quick Actions">
        <Space size="middle">
          <Button
            type="primary"
            icon={<CloudSyncOutlined />}
            onClick={() => onTabChange('connected')}
          >
            Configure Syncs
          </Button>
          <Button icon={<UploadOutlined />} onClick={() => onTabChange('manual')}>
            Download Export
          </Button>
          <Button icon={<SettingOutlined />} onClick={() => onTabChange('connected')}>
            Manage Connections
          </Button>
        </Space>
      </Card>
    </Space>
  )
}
